/**
 * Location & work order tags.
 *
 * Extracted verbatim from workOrdersService.ts — functions are invoked with
 * `this` bound to the WorkOrdersService instance (WorkOrdersCtx).
 */
import { extractItems } from '../../utils/apiHelpers';
// Tags API
export async function listLocationTags(locationId) {
    return this._dedup(`listLocationTags:${locationId}`, async () => {
        try {
            const response = await this.client.get(`/locations/${locationId}/tags`);
            if (!response.success) {
                return this.listFailure(response.error || 'Failed to load location tags');
            }
            // Empty/absent data is a valid "no tags" result, not a failure — a
            // tagless location returns [] (or, historically, null).
            const list = response.data ? extractItems(response.data, ['tags']) : [];
            return list.map((tag) => this.normalizeTag(tag)).filter(Boolean);
        }
        catch (error) {
            console.error('[WorkOrdersService] List location tags error:', error);
            return this.listFailure('Failed to load location tags', error);
        }
    });
}
export async function createLocationTag(locationId, payload) {
    try {
        const body = { name: payload.name.trim() };
        if (payload.color != null)
            body.color = payload.color;
        if (payload.description != null && payload.description !== '')
            body.description = payload.description;
        const response = await this.client.post(`/locations/${locationId}/tags`, body);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to create tag');
        }
        const tag = response.data.tag || response.data;
        const normalized = this.normalizeTag(tag);
        if (!normalized) {
            throw new Error('Failed to normalize created tag');
        }
        return normalized;
    }
    catch (error) {
        console.error('[WorkOrdersService] Create location tag error:', error);
        throw error;
    }
}
export async function updateLocationTag(locationId, tagId, payload) {
    try {
        const body = {};
        if (payload.name != null)
            body.name = payload.name.trim();
        if (payload.color !== undefined)
            body.color = payload.color;
        if (payload.description !== undefined)
            body.description = payload.description;
        const response = await this.client.put(`/locations/${locationId}/tags/${tagId}`, body);
        if (!response.success || !response.data) {
            throw new Error(response.error || 'Failed to update tag');
        }
        const tag = response.data.tag || response.data;
        const normalized = this.normalizeTag(tag);
        if (!normalized) {
            throw new Error('Failed to normalize updated tag');
        }
        return normalized;
    }
    catch (error) {
        console.error('[WorkOrdersService] Update location tag error:', error);
        throw error;
    }
}
export async function deleteLocationTag(locationId, tagId) {
    try {
        const response = await this.client.delete(`/locations/${locationId}/tags/${tagId}`);
        if (!response.success) {
            throw new Error(response.error || 'Failed to delete tag');
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Delete location tag error:', error);
        throw error;
    }
}
export async function getWorkOrderTags(workOrderId) {
    try {
        const response = await this.client.get(`/work-orders/${workOrderId}/tags`);
        if (!response.success || !response.data) {
            return this.listFailure(response.error || 'Failed to load work order tags');
        }
        const list = extractItems(response.data, ['tags']);
        return list.map((tag) => this.normalizeTag(tag)).filter(Boolean);
    }
    catch (error) {
        console.error('[WorkOrdersService] Get work order tags error:', error);
        return this.listFailure('Failed to load work order tags', error);
    }
}
export async function addTagsToWorkOrder(workOrderId, tagIds) {
    try {
        if (!tagIds.length)
            return;
        const response = await this.client.post(`/work-orders/${workOrderId}/tags`, { tag_ids: tagIds });
        if (!response.success) {
            throw new Error(response.error || 'Failed to add tags');
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Add tags to work order error:', error);
        throw error;
    }
}
export async function removeTagsFromWorkOrder(workOrderId, tagIds) {
    try {
        if (!tagIds.length)
            return;
        // The backend parses tag_ids from the request BODY (c.BodyParser), so
        // prefer a body-carrying DELETE when the client supports it. The
        // query-string form is kept only as a fallback for legacy clients.
        const response = this.client.deleteWithBody
            ? await this.client.deleteWithBody(`/work-orders/${workOrderId}/tags`, { tag_ids: tagIds })
            : await this.client.delete(`/work-orders/${workOrderId}/tags?tag_ids=${tagIds.join(',')}`);
        if (!response.success) {
            throw new Error(response.error || 'Failed to remove tags from work order');
        }
    }
    catch (error) {
        console.error('[WorkOrdersService] Remove tags from work order error:', error);
        throw error;
    }
}
/**
 * Replaces a work order's tags with exactly `tagIds` (PUT, one transaction)
 * and returns its tags afterwards. Refused with a code on the thrown error's
 * `errorBody`: WORK_ORDER_CLOSED, WORK_ORDER_INVOICED, TAGS_REQUIRED,
 * TAG_NOT_AT_LOCATION — see describeTagSaveError.
 */
export async function setWorkOrderTags(workOrderId, tagIds) {
    const response = await this.client.put(`/work-orders/${workOrderId}/tags`, { tag_ids: tagIds });
    if (!response.success) {
        const err = new Error(response.error || 'Failed to save tags');
        if (response.errorBody)
            err.errorBody = response.errorBody;
        throw err;
    }
    const list = extractItems(response.data, ['tags']);
    return list.map((tag) => this.normalizeTag(tag)).filter(Boolean);
}
